import { computed, ref, type Ref } from 'vue';
import type { AIInsights, Cluster, UserContribution } from '@/services/types/api.types';
import type { QNotifyCreateOptions } from 'quasar';

type Idea = Cluster['ideas'][number];

interface DragDropApi {
  moveIdeaToTheme: (idea: Idea, targetTheme: Cluster, sourceThemeName: string) => void;
  mergeThemes: (sourceTheme: Cluster, targetTheme: Cluster) => void;
  mergeIdeas: (sourceIdea: Idea, targetIdea: Idea) => void;
}

interface NotifyFn {
  (opts: QNotifyCreateOptions): void;
}

export function useIdeaActions(
  insights: Ref<AIInsights | null>,
  contributions: Ref<UserContribution[]>,
  dragDrop: DragDropApi,
  t: (key: string, params?: Record<string, any>) => string,
  notify: NotifyFn,
) {
  const moveDialog = ref(false);
  const moveTargetTheme = ref<string | null>(null);
  const moveIdeaContext = ref<{ sourceTheme: Cluster; idea: Idea } | null>(null);

  const mergeThemeDialog = ref(false);
  const mergeTargetTheme = ref<string | null>(null);
  const mergeSourceTheme = ref<Cluster | null>(null);

  const mergeIdeaDialog = ref(false);
  const mergeIdeaTarget = ref<{ cluster: Cluster; idea: Idea } | null>(null);
  const mergeIdeaContext = ref<{ sourceTheme: Cluster; idea: Idea } | null>(null);
  const mergeIdeaSearch = ref('');

  const contributionsDialog = ref(false);
  const contributionsContext = ref<{ cluster: Cluster; idea: Idea } | null>(null);
  const expandedContributions = ref<Set<string>>(new Set());

  const moveThemeOptions = computed(() => {
    const excludeTheme = moveIdeaContext.value?.sourceTheme.theme;
    return (insights.value?.clusters || [])
      .filter((cluster) => !excludeTheme || cluster.theme !== excludeTheme)
      .map((cluster) => ({ label: cluster.theme, value: cluster.theme }));
  });

  const mergeThemeOptions = computed(() => {
    const excludeTheme = mergeSourceTheme.value?.theme;
    return (insights.value?.clusters || [])
      .filter((cluster) => !excludeTheme || cluster.theme !== excludeTheme)
      .map((cluster) => ({ label: cluster.theme, value: cluster.theme }));
  });

  const mergeIdeaOptions = computed(() => {
    const sourceIdea = mergeIdeaContext.value?.idea;
    const term = mergeIdeaSearch.value.trim().toLowerCase();

    return (insights.value?.clusters || [])
      .flatMap((cluster) =>
        cluster.ideas
          .filter((idea) => idea !== sourceIdea)
          .map((idea) => ({
            id: buildIdeaId(cluster, idea),
            label: `${idea.representative_idea} — ${cluster.theme}`,
            description: t('consultation.ai.mergeIdeas.optionDescription', {
              count: idea.answer_ids.length,
              theme: cluster.theme,
            }),
            value: { cluster, idea },
          })),
      )
      .filter((option) => {
        if (!term) return true;
        return option.label.toLowerCase().includes(term) || (option.description || '').toLowerCase().includes(term);
      });
  });

  const mergeIdeaTargetId = computed(() =>
    mergeIdeaTarget.value ? buildIdeaId(mergeIdeaTarget.value.cluster, mergeIdeaTarget.value.idea) : null,
  );

  const missingContributionLog = new Set<string>();

  const contributionsById = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    contributions.value.forEach((c) => {
      map[String(c.answer_id)] = c.content;
    });
    return map;
  });

  const contributionsList = computed(() => {
    if (!contributionsContext.value) return [];
    const { idea } = contributionsContext.value;
    const resolved = idea.answer_ids.flatMap((id) => {
      const key = String(id);
      const content = contributionsById.value[key];

      if (content === undefined) {
        const logKey = `${idea.representative_idea}::${key}`;
        if (!missingContributionLog.has(logKey)) {
          missingContributionLog.add(logKey);
        }
        return [];
      }

      return [
        {
          id: key,
          content,
        },
      ];
    });

    return resolved;
  });

  function openMoveIdeaDialog(cluster: Cluster, idea: Idea) {
    moveIdeaContext.value = { sourceTheme: cluster, idea };
    moveTargetTheme.value = null;
    moveDialog.value = true;
  }

  function cancelMoveIdeaDialog() {
    moveDialog.value = false;
    moveTargetTheme.value = null;
    moveIdeaContext.value = null;
  }

  function confirmMoveIdea() {
    if (!moveIdeaContext.value || !moveTargetTheme.value) return;

    const targetTheme = insights.value?.clusters?.find((cluster) => cluster.theme === moveTargetTheme.value);
    if (!targetTheme) {
      notify({
        type: 'negative',
        message: t('consultation.ai.moveIdea.targetMissing'),
        position: 'top',
      });
      return;
    }

    dragDrop.moveIdeaToTheme(moveIdeaContext.value.idea, targetTheme, moveIdeaContext.value.sourceTheme.theme);
    cancelMoveIdeaDialog();
  }

  function openMergeThemeDialog(cluster: Cluster) {
    mergeSourceTheme.value = cluster;
    mergeTargetTheme.value = null;
    mergeThemeDialog.value = true;
  }

  function cancelMergeThemeDialog() {
    mergeThemeDialog.value = false;
    mergeTargetTheme.value = null;
    mergeSourceTheme.value = null;
  }

  function confirmMergeTheme() {
    if (!mergeSourceTheme.value || !mergeTargetTheme.value) return;

    const targetTheme = insights.value?.clusters?.find((cluster) => cluster.theme === mergeTargetTheme.value);
    if (!targetTheme) {
      notify({
        type: 'negative',
        message: t('consultation.ai.mergeThemes.targetMissing'),
        position: 'top',
      });
      return;
    }

    dragDrop.mergeThemes(mergeSourceTheme.value, targetTheme);
    cancelMergeThemeDialog();
  }

  function openMergeIdeaDialog(cluster: Cluster, idea: Idea) {
    mergeIdeaContext.value = { sourceTheme: cluster, idea };
    mergeIdeaTarget.value = null;
    mergeIdeaSearch.value = '';
    mergeIdeaDialog.value = true;
  }

  function cancelMergeIdeaDialog() {
    mergeIdeaDialog.value = false;
    mergeIdeaTarget.value = null;
    mergeIdeaContext.value = null;
  }

  function confirmMergeIdea() {
    if (!mergeIdeaContext.value || !mergeIdeaTarget.value) return;

    const { idea: sourceIdea } = mergeIdeaContext.value;
    const { idea: targetIdea, cluster } = mergeIdeaTarget.value;

    const stillExists = insights.value?.clusters?.some((c) => c === cluster && c.ideas.includes(targetIdea)) ?? false;

    if (!stillExists) {
      notify({
        type: 'negative',
        message: t('consultation.ai.mergeIdeas.targetMissing'),
        position: 'top',
      });
      return;
    }

    dragDrop.mergeIdeas(sourceIdea, targetIdea);
    cancelMergeIdeaDialog();
  }

  function buildIdeaId(cluster: Cluster, idea: Idea) {
    return `${cluster.theme}:::${idea.representative_idea}`;
  }

  function openContributionsDialog(cluster: Cluster, idea: Idea) {
    contributionsContext.value = { cluster, idea };
    expandedContributions.value = new Set();
    contributionsDialog.value = true;
  }

  function closeContributionsDialog() {
    contributionsDialog.value = false;
    contributionsContext.value = null;
    expandedContributions.value = new Set();
  }

  function toggleContribution(id: string) {
    const next = new Set(expandedContributions.value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    expandedContributions.value = next;
  }

  return {
    moveDialog,
    moveIdeaContext,
    moveTargetTheme,
    moveThemeOptions,
    mergeThemeDialog,
    mergeThemeOptions,
    mergeTargetTheme,
    mergeSourceTheme,
    mergeIdeaDialog,
    mergeIdeaOptions,
    mergeIdeaTarget,
    mergeIdeaTargetId,
    mergeIdeaContext,
    mergeIdeaSearch,
    contributionsDialog,
    contributionsContext,
    contributionsList,
    expandedContributions,
    openMoveIdeaDialog,
    cancelMoveIdeaDialog,
    confirmMoveIdea,
    openMergeThemeDialog,
    cancelMergeThemeDialog,
    confirmMergeTheme,
    openMergeIdeaDialog,
    cancelMergeIdeaDialog,
    confirmMergeIdea,
    openContributionsDialog,
    closeContributionsDialog,
    toggleContribution,
  };
}
